/**
 * PATH: apps/quote/src/pages/claims/claim-editor-page/ClaimSummary.tsx
 * PURPOSE: Summary panel showing claim totals and progress
 */

import { useEffect, useState } from 'react'
import { formatCurrency } from '@/lib/utils'
import type { PaymentClaimDetail } from './types'

interface Props {
  claim: PaymentClaimDetail
  /** Present only while the claim is editable; retention $ math stays server-side. */
  onRetentionChange?: (percent: number) => void
  isRetentionSaving?: boolean
}

/** Inline retention % editor — commits on blur/Enter, clamped 0–100. */
function RetentionControl({ value, onCommit, saving }: { value: string | null; onCommit: (p: number) => void; saving: boolean }) {
  const [draft, setDraft] = useState(value ?? '0')
  useEffect(() => { setDraft(value ?? '0') }, [value])

  const commit = () => {
    const parsed = parseFloat(draft)
    if (Number.isNaN(parsed)) { setDraft(value ?? '0'); return }
    const clamped = Math.min(100, Math.max(0, parsed))
    if (clamped !== parseFloat(value ?? '0')) onCommit(clamped)
  }

  return (
    <span className="inline-flex items-center gap-1">
      <input
        type="number"
        min={0}
        max={100}
        step={0.5}
        value={draft}
        disabled={saving}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
        aria-label="Retention percent"
        className="w-16 px-1.5 py-0.5 text-right text-sm font-mono rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50"
      />
      <span className="text-slate-500 text-sm">%</span>
    </span>
  )
}

export default function ClaimSummary({ claim, onRetentionChange, isRetentionSaving = false }: Props) {
  const retentionPercent = parseFloat(claim.retention_percent ?? '0') || 0
  const showRetention = onRetentionChange !== undefined || retentionPercent > 0
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Claim Summary</h3>

      <div className="space-y-3">
        <div className="flex justify-between">
          <span className="text-slate-600 dark:text-slate-400">Contract Value</span>
          <span className="font-mono">{formatCurrency(parseFloat(claim.contract_value))}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-600 dark:text-slate-400">Approved Variations</span>
          <span className="font-mono">{formatCurrency(parseFloat(claim.approved_variations_value))}</span>
        </div>
        <div className="flex justify-between font-medium border-t border-slate-200 dark:border-slate-700 pt-3">
          <span>Revised Contract Value</span>
          <span className="font-mono">{formatCurrency(parseFloat(claim.revised_contract_value))}</span>
        </div>

        <div className="border-t border-slate-200 dark:border-slate-700 pt-3 space-y-3">
          <div className="flex justify-between text-slate-600 dark:text-slate-400">
            <span>Previously Claimed</span>
            <span className="font-mono">{formatCurrency(parseFloat(claim.previously_claimed))}</span>
          </div>
          <div className="flex justify-between text-amber-600 dark:text-amber-400 font-medium">
            <span>This Claim (ex GST)</span>
            <span className="font-mono">{formatCurrency(parseFloat(claim.this_claim))}</span>
          </div>
          {showRetention && (
            <>
              <div className="flex justify-between items-center text-slate-600 dark:text-slate-400">
                <span>Retention (withheld before GST)</span>
                {onRetentionChange
                  ? <RetentionControl value={claim.retention_percent} onCommit={onRetentionChange} saving={isRetentionSaving} />
                  : <span className="font-mono">{retentionPercent.toFixed(2)}%</span>}
              </div>
              {claim.retention_this_claim != null && parseFloat(claim.retention_this_claim) !== 0 && (
                <div className="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Retention withheld</span>
                  <span className="font-mono">-{formatCurrency(parseFloat(claim.retention_this_claim))}</span>
                </div>
              )}
              {claim.net_claim_amount != null && retentionPercent > 0 && (
                <div className="flex justify-between text-slate-700 dark:text-slate-300 font-medium">
                  <span>Net claim (ex GST)</span>
                  <span className="font-mono">{formatCurrency(parseFloat(claim.net_claim_amount))}</span>
                </div>
              )}
            </>
          )}
          <div className="flex justify-between text-slate-600 dark:text-slate-400">
            <span>GST ({(parseFloat(claim.gst_rate) * 100).toFixed(0)}%)</span>
            <span className="font-mono">{formatCurrency(parseFloat(claim.this_claim_gst))}</span>
          </div>
          <div className="flex justify-between text-lg font-bold bg-amber-50 dark:bg-amber-900/20 -mx-6 px-6 py-3">
            <span>This Claim (inc GST)</span>
            <span className="font-mono text-amber-600 dark:text-amber-400">{formatCurrency(parseFloat(claim.this_claim_incl_gst))}</span>
          </div>
        </div>

        <div className="border-t border-slate-200 dark:border-slate-700 pt-3 space-y-3">
          <div className="flex justify-between">
            <span className="text-slate-600 dark:text-slate-400">Total Claimed</span>
            <span className="font-mono">{formatCurrency(parseFloat(claim.total_claimed))}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-600 dark:text-slate-400">Balance Remaining</span>
            <span className="font-mono">{formatCurrency(parseFloat(claim.balance_remaining))}</span>
          </div>
          <div className="pt-2">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-500">Progress</span>
              <span className="font-medium">{claim.progress_percent.toFixed(1)}%</span>
            </div>
            <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full transition-all duration-300" style={{ width: `${Math.min(100, claim.progress_percent)}%` }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
